/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useImperativeHandle, useState, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Stack, Typography, Chip, TextField, Alert, Collapse,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import Iconify from '../Iconify';
import DentallyPatientMapper from './DentallyPatientMapper';
import { parseDentallyDate } from '../../utils/dentallyUtils';
import { useSelector, dispatch } from '../../redux/store';
import {
    setPendingDentallySync,
    setDentallyPatientId,
    addReminder,
} from '../../redux/slices/lead';
import useAuth from '../../hooks/useAuth';
import {
    journeyContainerStyle,
    journeySummaryBarStyle,
    journeyStepIndicator,
    journeyConnectorStyle,
    journeyStepCardStyle,
    journeyStepCollapsedStyle,
    journeyPaymentBtnStyle,
    journeyLinkedBadgeStyle,
    presetChipGroupStyle,
    presetChipStyle,
} from '../../styles/DentallyBookingStyle';

const REMINDER_PRESETS = [
    { key: '30min', label: '30 min', minutes: 30 },
    { key: '1hr', label: '1 hour', minutes: 60 },
    { key: '2hr', label: '2 hours', minutes: 120 },
    { key: '4hr', label: '4 hours', minutes: 240 },
    { key: 'custom', label: 'Custom', minutes: null },
];

const DentallyPaymentConfirmation = forwardRef(({ handleNext }, ref) => {
    const { user } = useAuth();
    const { dentallySlot, newLeadDetail, leadDetail, tableRowLead, followLead } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const practiceUser = useSelector((state) => state.practiceUser?.practiceUser);

    // Derive lead info — match the same fallback chain used by other wizard steps
    const leadId = leadDetail?.id || followLead?.id || newLeadDetail?.personalDetail?.lead_id
        || tableRowLead?.lead_id || tableRowLead?.id || newLeadDetail?.id || '';
    const treatmentId = newLeadDetail?.personalDetail?.lead_treatment_id
        || leadDetail?.lead_treatments?.[0]?.id || followLead?.lead_treatments?.[0]?.id || '';
    const leadName = [
        leadDetail?.first_name || followLead?.first_name || newLeadDetail?.personalDetail?.first_name || tableRowLead?.first_name || '',
        leadDetail?.last_name || followLead?.last_name || newLeadDetail?.personalDetail?.last_name || tableRowLead?.last_name || '',
    ].filter(Boolean).join(' ') || 'Patient';
    const leadMetadata = leadDetail?.lead_metadata || followLead?.lead_metadata || tableRowLead?.lead_metadata || [];

    // Journey state: 'linking' → 'payment'
    const [journeyPhase, setJourneyPhase] = useState('linking');
    const [mappedPatientId, setMappedPatientId] = useState(null);

    // Payment state
    const [reminderPreset, setReminderPreset] = useState('1hr');
    const [customReminderDate, setCustomReminderDate] = useState(null);
    const [reminderNote, setReminderNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Ref for auto-scrolling to Step 2
    const paymentStepRef = useRef(null);

    // Set default reminder note
    useMemo(() => {
        if (dentallySlot?.practitioner_name) {
            setReminderNote(`Sync Dentally appointment for ${leadName} with ${dentallySlot.practitioner_name}`);
        }
    }, [dentallySlot?.practitioner_name, leadName]);

    // Compute reminder datetime from preset (DD-MM-YYYY HH:mm:ss.0Z format)
    const getReminderDatetime = () => {
        if (reminderPreset === 'custom') {
            return customReminderDate ? `${moment(customReminderDate).format('DD-MM-YYYY HH:mm:ss')}.0Z` : null;
        }
        const preset = REMINDER_PRESETS.find(p => p.key === reminderPreset);
        if (!preset?.minutes) return null;
        const d = new Date();
        d.setMinutes(d.getMinutes() + preset.minutes);
        return `${moment(d).format('DD-MM-YYYY HH:mm:ss')}.0Z`;
    };

    // ── Patient mapped callback ──────────────────────────────────────────
    const handlePatientMapped = useCallback((patientId) => {
        setMappedPatientId(patientId);
        setError('');
        dispatch(setDentallyPatientId(patientId));
        // Auto-advance to payment phase
        setJourneyPhase('payment');
        // Scroll to payment step after collapse animation
        setTimeout(() => {
            paymentStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 400);
    }, []);

    // ── Payment Received — auto-advance ──────────────────────────────────
    const handlePaymentReceived = () => {
        if (!mappedPatientId) {
            setError('Please link a Dentally patient first.');
            return;
        }
        setIsSubmitting(true);
        dispatch(setDentallyPatientId(mappedPatientId));
        dispatch(setPendingDentallySync('complete'));
        // Metadata patch happens in MessageAndReminder on finish — not here
        handleNext(true);
    };

    // ── Payment Not Received — set reminder then advance ─────────────────
    const handlePaymentNotReceived = () => {
        if (!mappedPatientId) {
            setError('Please link a Dentally patient first.');
            return;
        }
        const reminderDt = getReminderDatetime();
        if (!reminderDt) {
            setError('Please select a reminder time.');
            return;
        }
        setIsSubmitting(true);

        // Resolve current auth user to practice user ID for assignees
        let assigneeIds = [];
        if (user?.pk) {
            const currentPracticeUser = practiceUser?.results?.find(el => el?.user?.id === user.pk);
            if (currentPracticeUser?.id) assigneeIds = [currentPracticeUser.id];
        }
        const reminderPayload = {
            method: 'CALL',
            datetime: reminderDt,
            note: reminderNote || `Sync Dentally appointment for ${leadName}`,
            assignees: assigneeIds,
            is_completed: false,
            lead: leadId,
            lead_treatment: treatmentId || undefined,
            group: 'TODO',
            priority: 'URGENT',
        };
        dispatch(addReminder(reminderPayload, practiceId, () => {}, 'createLead'));

        dispatch(setDentallyPatientId(mappedPatientId));
        dispatch(setPendingDentallySync('yes'));
        // Metadata patch happens in MessageAndReminder on finish — not here
        handleNext(true);
    };

    // ── Expose submit() for parent's Next button (fallback) ──────────────
    useImperativeHandle(
        ref, () => ({
            async submit() {
                // If already in payment phase with a mapped patient, treat as "received"
                if (mappedPatientId && journeyPhase === 'payment') {
                    handlePaymentReceived();
                    return;
                }
                if (!mappedPatientId) {
                    setError('Please link a Dentally patient before continuing.');
                    return;
                }
                handleNext(true);
            },
        }),
        [mappedPatientId, journeyPhase, practiceId, leadId]
    );

    // ── Slot summary info ────────────────────────────────────────────────
    const slotDate = dentallySlot?.start_time
        ? (parseDentallyDate(dentallySlot.start_time) || new Date(dentallySlot.start_time)).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        }) : '—';
    const slotTime = dentallySlot?.start_time
        ? (parseDentallyDate(dentallySlot.start_time) || new Date(dentallySlot.start_time)).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
        : '—';
    const slotEndTime = dentallySlot?.finish_time
        ? (parseDentallyDate(dentallySlot.finish_time) || new Date(dentallySlot.finish_time)).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
        : '—';

    // Step statuses
    const isLinked = journeyPhase === 'payment';
    const linkStatus = isLinked ? 'completed' : 'active';
    const paymentStatus = isLinked ? 'active' : 'pending';

    return (
        <Box sx={journeyContainerStyle}>

            {/* ── Compact Appointment Summary ── */}
            <Box sx={journeySummaryBarStyle}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Iconify icon='mdi:calendar-check' width={18} sx={{ color: '#fff' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant='body2' noWrap sx={{ fontWeight: 700, fontSize: 13 }}>
                        {dentallySlot?.practitioner_name || 'Practitioner'}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontFamily: '"SF Mono", monospace', fontSize: 11 }}>
                        {slotDate} &bull; {slotTime} — {slotEndTime}
                    </Typography>
                </Box>
            </Box>

            {/* ── Step 1: Link Patient ── */}
            <Stack direction='row' spacing={1.5} sx={{ mb: 0.5 }}>
                {/* Step indicator */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={journeyStepIndicator(linkStatus)}>
                        {isLinked
                            ? <Iconify icon='mdi:check-bold' width={16} sx={{ color: '#fff' }} />
                            : <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>1</Typography>
                        }
                    </Box>
                    <Box sx={journeyConnectorStyle(isLinked)} />
                </Box>

                {/* Step content */}
                <Box sx={{ flex: 1, pb: 1 }}>
                    <Typography variant='subtitle2' sx={{
                        fontWeight: 700, fontSize: 13, mb: 0.75,
                        color: isLinked ? 'success.dark' : 'text.primary',
                    }}>
                        Link Dentally Patient
                    </Typography>

                    {/* Expanded: full mapper UI (when linking) */}
                    <Collapse in={!isLinked} timeout={350}>
                        <Box sx={journeyStepCardStyle('active')}>
                            <DentallyPatientMapper
                                leadId={leadId}
                                practiceId={practiceId}
                                leadMetadata={leadMetadata}
                                onPatientMapped={handlePatientMapped}
                            />
                        </Box>
                    </Collapse>

                    {/* Collapsed: compact linked badge (after linking) */}
                    <Collapse in={isLinked} timeout={350}>
                        <Box sx={journeyStepCollapsedStyle}>
                            <Box sx={journeyLinkedBadgeStyle}>
                                <Box sx={{
                                    width: 24, height: 24, borderRadius: '50%',
                                    bgcolor: 'rgba(34,197,94,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Iconify icon='mdi:check-bold' width={14} sx={{ color: 'success.main' }} />
                                </Box>
                                <Typography variant='body2' sx={{ fontWeight: 700, fontSize: 12, color: 'success.dark' }}>
                                    Linked to Dentally Patient #{mappedPatientId}
                                </Typography>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Stack>

            {/* ── Step 2: Payment Decision ── */}
            <Box ref={paymentStepRef}>
                <Stack direction='row' spacing={1.5}>
                    {/* Step indicator */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={journeyStepIndicator(paymentStatus)}>
                            <Typography sx={{
                                color: paymentStatus === 'active' ? '#fff' : 'text.disabled',
                                fontSize: 12, fontWeight: 800,
                            }}>
                                2
                            </Typography>
                        </Box>
                    </Box>

                    {/* Step content */}
                    <Box sx={{ flex: 1, opacity: paymentStatus === 'pending' ? 0.4 : 1, transition: 'opacity 0.3s ease' }}>
                        <Typography variant='subtitle2' sx={{
                            fontWeight: 700, fontSize: 13, mb: 0.75,
                            color: paymentStatus === 'pending' ? 'text.disabled' : 'text.primary',
                        }}>
                            Confirm Payment
                        </Typography>

                        <Collapse in={paymentStatus === 'active'} timeout={400}>
                            <Box sx={journeyStepCardStyle('active')}>
                                <Stack spacing={1.5}>
                                    {/* Payment Received */}
                                    <Box
                                        onClick={handlePaymentReceived}
                                        sx={journeyPaymentBtnStyle('received', false)}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: 1.5,
                                            bgcolor: 'rgba(34,197,94,0.1)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <Iconify icon='mdi:check-circle-outline' width={22} sx={{ color: '#22C55E' }} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant='subtitle2' sx={{ fontWeight: 700, fontSize: 13 }}>
                                                Payment Received
                                            </Typography>
                                            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
                                                Dentally appointment will be created on finish
                                            </Typography>
                                        </Box>
                                        <Iconify icon='mdi:arrow-right' width={18} sx={{ color: '#22C55E' }} />
                                    </Box>

                                    {/* Payment Not Received */}
                                    <Box sx={{
                                        borderRadius: 2,
                                        border: '2px solid #FFAB00',
                                        overflow: 'hidden',
                                    }}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box sx={{
                                                width: 36, height: 36, borderRadius: 1.5,
                                                bgcolor: 'rgba(255,171,0,0.1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <Iconify icon='mdi:clock-alert-outline' width={22} sx={{ color: '#FFAB00' }} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant='subtitle2' sx={{ fontWeight: 700, fontSize: 13 }}>
                                                    Payment Not Received
                                                </Typography>
                                                <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
                                                    Set a reminder to follow up and sync later
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Reminder options */}
                                        <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: '1px solid rgba(255,171,0,0.15)' }}>
                                            <Typography sx={{
                                                fontSize: '10px', fontWeight: 700, color: 'text.disabled',
                                                textTransform: 'uppercase', letterSpacing: '0.8px',
                                                mb: 1, mt: 0.5,
                                            }}>
                                                Remind me in
                                            </Typography>
                                            <Box sx={{ ...presetChipGroupStyle, mb: 1.5 }}>
                                                {REMINDER_PRESETS.map(({ key, label }) => (
                                                    <Chip
                                                        key={key}
                                                        label={label}
                                                        size='small'
                                                        variant={reminderPreset === key ? 'filled' : 'outlined'}
                                                        onClick={() => setReminderPreset(key)}
                                                        sx={presetChipStyle(reminderPreset === key)}
                                                    />
                                                ))}
                                            </Box>
                                            {reminderPreset === 'custom' && (
                                                <DateTimePicker
                                                    label='Reminder date & time'
                                                    value={customReminderDate}
                                                    onChange={setCustomReminderDate}
                                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mb: 1.5 } } }}
                                                    minDateTime={new Date()}
                                                />
                                            )}
                                            <TextField
                                                fullWidth
                                                size='small'
                                                label='Reminder note'
                                                value={reminderNote}
                                                onChange={(e) => setReminderNote(e.target.value)}
                                                multiline
                                                rows={2}
                                                sx={{
                                                    mb: 1.5,
                                                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                                                }}
                                            />
                                            <LoadingButton
                                                fullWidth
                                                variant='contained'
                                                loading={isSubmitting}
                                                onClick={handlePaymentNotReceived}
                                                startIcon={<Iconify icon='mdi:bell-plus-outline' width={18} />}
                                                sx={{
                                                    borderRadius: '10px',
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                    background: 'linear-gradient(135deg, #FFAB00 0%, #e09600 100%)',
                                                    color: '#fff',
                                                    boxShadow: '0 2px 8px rgba(255,171,0,0.3)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #ffc233 0%, #FFAB00 100%)',
                                                    },
                                                }}
                                            >
                                                Set Reminder & Continue
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        </Collapse>

                        <Collapse in={paymentStatus === 'pending'} timeout={300}>
                            <Box sx={{
                                ...journeyStepCardStyle('pending'),
                                py: 2.5,
                                textAlign: 'center',
                            }}>
                                <Iconify icon='mdi:lock-outline' width={20} sx={{ color: 'text.disabled', mb: 0.5 }} />
                                <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: 11, display: 'block' }}>
                                    Complete patient linking to unlock this step
                                </Typography>
                            </Box>
                        </Collapse>
                    </Box>
                </Stack>
            </Box>

            {/* ── Error display ── */}
            {error && (
                <Alert severity='error' sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
});

DentallyPaymentConfirmation.propTypes = {
    handleNext: PropTypes.func.isRequired,
};

DentallyPaymentConfirmation.displayName = 'DentallyPaymentConfirmation';

export default DentallyPaymentConfirmation;
